import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useExperiment } from "../../contexts/ExperimentContext";
import { useTracker } from "../../contexts/TrackerContext";
import { savePhaseData } from "../../services/api";
import { getOrgConfig } from "../../data/orgConfigs";

// ---------------------------
// Types
// ---------------------------
interface TimingEntry {
  itemId: string;
  startTime: number;
  responseTime: number; // ms
}

interface PostTaskBatteryData {
  // Section 0: Decision Approach (exploratory - individual differences in gist/verbatim tendency)
  R1: number;
  R2: number;
  R3: number;
  // DELETED SECTIONS (not tied to H1-H4):
  // E1, E2: Error Preferences (Section 1) - SDT, not SCT
  // T1: Familiar Templates (Section 2) - weak theory link
  // V1, V2, V3: Tool Ease (Section 3) - irrelevant
  // C1, C2: Conflicting Cues (Section 4) - redundant with Section 7 manipulation checks
  // B1: Email Experience (Section 5) - single exploratory question
  // P1, P2: Learning sections - previously removed

  // Student identification
  studentEmail: string;

  // Demographics
  age: string;
  gender: string;
  education: string;
  computerExpertise: string;
  emailUsageFrequency: string;
  organizationSize: string;
  industry: string;
  jobRole: string;
  otherJobRole?: string; // For when jobRole is "other"
  otherIndustry?: string; // For when industry is "other"
  // Security experience (existing)
  phishingExperience: string;
  securityTraining: string;
  // Email expertise measures (NEW - for H4 expertise moderation)
  emailHoursPerWeek: string;
  phishingConfidence: string;
  phishingIdentified: string;
  securityRole: string;
  // Manipulation checks and method bias
  taskDifficulty: string;
  congruenceAwareness: string;
  musicEnjoyment: number;
  // NEW CRITICAL MANIPULATION CHECKS
  cueConflictNoticed: string;
  cueConflictResolution: string;
  actualProcessingMode: string;
  scenarioDifficultyType: string;
  // PROCESS MEASURES
  gistExtractionDifficulty: string; // How hard was it to form overall impressions
  postTaskConfidence: number; // 1-7 Likert: confidence their answers were correct
  responseTimings: {
    startTime: number;
    itemTimings: TimingEntry[];
    totalTime: number;
  };
}

interface DerivedScores {
  alpha_route: number; // Section 0: General gist/verbatim tendency (R1, R2, R3)
  alpha_route_01: number; // Normalized to 0-1
  // REMOVED (not tied to H1-H4):
  // rho_error_cost, criterion_delete_bias (from E1, E2 - Section 1)
  // tau_templates (from T1 - Section 2)
  // phi_verif_friction (from V1, V2 - Section 3)
  // kappa_conflict (from C1, C2 - Section 4)
  // pi_plasticity (from P1, P2 - previously removed)
  // p_hat_base_rate (from B1 - Section 5)
}

// ---------------------------
// Helpers
// ---------------------------
// Removed clamp function (only used for deleted sections E1, B1)

const Likert7 = [
  { value: 1, label: "Strongly\nDisagree" },
  { value: 2, label: "Disagree" },
  { value: 3, label: "Somewhat\nDisagree" },
  { value: 4, label: "Neither Agree\nnor Disagree" },
  { value: 5, label: "Somewhat\nAgree" },
  { value: 6, label: "Agree" },
  { value: 7, label: "Strongly\nAgree" }
];

// Removed EMAIL_TYPES constant (was for T1 - Familiar Templates section, now deleted)

function computeDerivedScores(d: PostTaskBatteryData): DerivedScores {
  const alpha_route = (d.R1 + (8 - d.R2) + d.R3) / 3;
  return {
    alpha_route,
    alpha_route_01: (alpha_route - 1) / 6
    // All other derived scores removed (not tied to H1-H4)
  };
}

// ---------------------------
// Styles (kept inline to avoid external CSS deps)
// ---------------------------
const S = {
  container: {
    backgroundColor: "#f8f9fa",
    minHeight: "100vh",
    padding: "24px",
    maxWidth: "900px",
    margin: "0 auto"
  },
  card: {
    backgroundColor: "white",
    padding: "40px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    marginBottom: "24px"
  },
  section: {
    marginBottom: "40px",
    paddingBottom: "32px",
    borderBottom: "2px solid #f0f0f0"
  },
  h2: { color: "#2c3e50", marginBottom: "16px", fontSize: "32px", fontWeight: 700 as const },
  h3: { color: "#2c3e50", marginBottom: "24px", fontSize: "24px", fontWeight: 600 as const },
  q: { fontSize: "16px", fontWeight: 500 as const, color: "#2c3e50", marginBottom: "20px", lineHeight: 1.5 },
  likertGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "12px",
    backgroundColor: "#f8f9fa",
    padding: "20px",
    borderRadius: "8px",
    border: "2px solid #e9ecef"
  },
  likertCard: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "8px",
    padding: "12px 8px",
    backgroundColor: "white",
    borderRadius: "6px",
    border: "1px solid #dee2e6",
    transition: "all 0.2s ease",
    cursor: "pointer"
  },
  likertLabel: {
    fontSize: "11px",
    textAlign: "center" as const,
    color: "#6c757d",
    fontWeight: 500 as const,
    lineHeight: 1.3,
    whiteSpace: "pre-line" as const
  },
  sliderWrap: {
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "2px solid #e9ecef"
  },
  slider: { width: "100%", height: "8px", borderRadius: "4px", outline: "none", background: "#e9ecef", cursor: "pointer", marginBottom: "12px" },
  sliderLabels: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#6c757d", fontWeight: 500 as const, marginBottom: "12px" },
  sliderValue: { textAlign: "center" as const, fontSize: "18px", fontWeight: 600 as const, color: "#0066cc", marginTop: "8px" },
  gridChecks: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "12px",
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "2px solid #e9ecef"
  },
  checkRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    backgroundColor: "white",
    borderRadius: "6px",
    border: "1px solid #dee2e6",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  numberWrap: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "2px solid #e9ecef"
  },
  numberInput: { width: "150px", padding: "12px 16px", border: "2px solid #e9ecef", borderRadius: "6px", fontSize: "16px", textAlign: "center" as const, fontWeight: 500 as const },
  bar: { width: "100%", height: "6px", backgroundColor: "#e9ecef", borderRadius: "3px", marginBottom: "24px", overflow: "hidden" },
  fill: { height: "100%", backgroundColor: "#0066cc", transition: "width 0.3s ease" },
  rowNav: { marginTop: "32px", display: "flex", justifyContent: "space-between", gap: "16px" },
  btn: { padding: "14px 32px", border: "none", borderRadius: "8px", fontSize: "16px", fontWeight: 600 as const, cursor: "pointer", transition: "all 0.2s ease", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" },
  btnPrimary: { backgroundColor: "#0066cc", color: "white" },
  btnSecondary: { backgroundColor: "#6c757d", color: "white" },
  error: { marginBottom: "24px", padding: "16px", backgroundColor: "#fff5f5", color: "#c53030", borderRadius: "8px", border: "2px solid #feb2b2" }
};

// Small components
function SelectQuestion({ name, value, onChange, options }: { 
  name: string; 
  value: string; 
  onChange: (v: string) => void; 
  options: Array<{value: string; label: string}> 
}) {
  return (
    <select 
      id={name}
      value={value} 
      onChange={e => onChange(e.target.value)}
      style={{
        width: '100%',
        padding: '12px 16px',
        fontSize: '16px',
        border: '2px solid #e9ecef',
        borderRadius: '8px',
        backgroundColor: 'white',
        cursor: 'pointer'
      }}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

function LikertQuestion({ name, value, onChange }: { name: string; value: number; onChange: (v: number) => void }) {
  return (
      <div style={S.likertGrid} role="radiogroup" aria-labelledby={`${name}-label`}>
        {Likert7.map(opt => {
          const id = `${name}_${opt.value}`;
          const active = value === opt.value;
          return (
              <label key={id} htmlFor={id} style={{ ...S.likertCard, backgroundColor: active ? "#e3f2fd" : "white", borderColor: active ? "#0066cc" : "#dee2e6" }}>
                <input
                    id={id}
                    type="radio"
                    name={name}
                    value={opt.value}
                    checked={active}
                    onChange={() => onChange(opt.value)}
                    onFocus={() => onChange(value || opt.value)}
                    style={{ margin: 0 }}
                />
                <span style={S.likertLabel}>{opt.label}</span>
              </label>
          );
        })}
      </div>
  );
}

// Removed CheckboxGrid component (was only used for T1 - Familiar Templates section, now deleted)

// ---------------------------
// Main component
// ---------------------------
const PostTaskBattery: React.FC = () => {
  const navigate = useNavigate();
  const { updateProgress } = useExperiment();
  const { tracker } = useTracker();

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [startTime] = useState(() => Date.now());
  const [itemStartTimes, setItemStartTimes] = useState<Record<string, number>>({});
  const [itemTimings, setItemTimings] = useState<TimingEntry[]>([]);

  const totalSections = 4; // Sections: 0=Decision Approach, 1=Demographics, 2=Expertise, 3=Manipulation Checks

  const [formData, setFormData] = useState<PostTaskBatteryData>({
    R1: 0, R2: 0, R3: 0,
    // Removed E1, E2, T1, C1, C2, B1 (Sections 1-5 deleted)
    // Student identification
    studentEmail: '',
    // Demographics defaults
    age: '',
    gender: '',
    education: '',
    computerExpertise: '',
    emailUsageFrequency: '',
    organizationSize: '',
    industry: '',
    jobRole: '',
    // Security experience defaults
    phishingExperience: '',
    securityTraining: '',
    // Email expertise defaults (NEW)
    emailHoursPerWeek: '',
    phishingConfidence: '',
    phishingIdentified: '',
    securityRole: '',
    // Manipulation checks and method bias defaults
    taskDifficulty: '',
    congruenceAwareness: '',
    musicEnjoyment: 0,
    cueConflictNoticed: '',
    cueConflictResolution: '',
    actualProcessingMode: '',
    scenarioDifficultyType: '',
    gistExtractionDifficulty: '',
    postTaskConfidence: 0,
    responseTimings: { startTime, itemTimings: [], totalTime: 0 }
  });

  const beginTiming = (id: string) => {
    setItemStartTimes(prev => (prev[id] ? prev : { ...prev, [id]: Date.now() }));
  };

  const stampTiming = (id: string) => {
    const st = itemStartTimes[id] || Date.now();
    const rt = Date.now() - st;
    setItemTimings(prev => {
      const others = prev.filter(t => t.itemId !== id);
      return [...others, { itemId: id, startTime: st, responseTime: rt }];
    });
  };

  const onLikert = (key: keyof PostTaskBatteryData, v: number) => {
    beginTiming(String(key));
    setFormData(prev => ({ ...prev, [key]: v }));
    stampTiming(String(key));
  };

  // Removed onSlider, onNumber, onMulti handlers (only used for deleted sections E1, B1, T1)

  const onSelect = (key: keyof PostTaskBatteryData, value: string) => {
    beginTiming(String(key));
    setFormData(prev => ({ ...prev, [key]: value }));
    stampTiming(String(key));
  };

  const validateSection = (sec: number): boolean => {
    switch (sec) {
      case 0: return formData.R1 > 0 && formData.R2 > 0 && formData.R3 > 0; // Decision Approach
      // DELETED: case 1 (E1, E2), case 2 (T1), case 3 (C1, C2), case 4 (B1)
      case 1: // Demographics validation (was case 5)
        const jobRoleValid = formData.jobRole !== '' &&
          (formData.jobRole !== 'other' || (!!formData.otherJobRole && formData.otherJobRole.trim() !== ''));
        const industryValid = formData.industry !== '' &&
          (formData.industry !== 'other' || (!!formData.otherIndustry && formData.otherIndustry.trim() !== ''));
        const basicFieldsValid = formData.age !== '' && formData.gender !== '' && formData.education !== '' &&
               formData.computerExpertise !== '' && formData.emailUsageFrequency !== '' &&
               formData.organizationSize !== '';
        return basicFieldsValid && industryValid && jobRoleValid;
      case 2: // Security experience + Email expertise validation (was case 6, now case 2)
        // Student email required for UCCS (extra credit tracking), optional for other orgs
        const orgCfg = getOrgConfig();
        const emailValid = orgCfg.orgId === 'uccs'
          ? (formData.studentEmail !== '' && formData.studentEmail.includes('@'))
          : true; // skip email validation for non-UCCS orgs
        return emailValid && formData.phishingExperience !== '' && formData.securityTraining !== '' &&
               formData.emailHoursPerWeek !== '' && formData.phishingConfidence !== '' &&
               formData.phishingIdentified !== '' && formData.securityRole !== '';
      case 3: // Manipulation checks and method bias validation (was case 7, now case 3)
        const basicChecksValid = formData.taskDifficulty !== '' &&
                                 formData.congruenceAwareness !== '' &&
                                 formData.cueConflictNoticed !== '' &&
                                 formData.actualProcessingMode !== '' &&
                                 formData.scenarioDifficultyType !== '' &&
                                 formData.gistExtractionDifficulty !== '' &&
                                 formData.postTaskConfidence > 0 &&
                                 formData.musicEnjoyment > 0;
        // Conditional validation: cueConflictResolution required only if cueConflictNoticed is 'yes-many' or 'yes-some'
        const conflictResolutionValid = ['yes-many', 'yes-some'].includes(formData.cueConflictNoticed)
          ? formData.cueConflictResolution !== ''
          : true; // Not required if they didn't notice conflicts
        return basicChecksValid && conflictResolutionValid;
      default: return true;
    }
  };

  const next = () => {
    if (!validateSection(currentSection)) {
      setError("Please complete all required fields in this section.");
      return;
    }
    setError(null);
    if (currentSection < totalSections - 1) setCurrentSection(s => s + 1);
    window.scrollTo(0, 0);
  };

  const prev = () => {
    if (currentSection > 0) setCurrentSection(s => s - 1);
    window.scrollTo(0, 0);
  };

  const submit = async () => {
    if (!validateSection(currentSection)) {
      setError("Please complete all required fields.");
      return;
    }

    // Show saving indicator
    setSaving(true);
    setError(null);

    const totalTime = Date.now() - startTime;
    const finalData: PostTaskBatteryData = {
      ...formData,
      responseTimings: { startTime, itemTimings, totalTime }
    };

    const derived = computeDerivedScores(finalData);

    const participantId = localStorage.getItem("participantId");
    if (participantId) {
      try {
        // CRITICAL: Wait for save to complete before navigating
        await savePhaseData(participantId, "post-task-battery", {
          rawResponses: finalData,
          derivedScores: derived,
          metadata: {
            completionTime: new Date().toISOString(),
            totalResponseTime: totalTime,
            averageItemTime: itemTimings.length ? itemTimings.reduce((s, t) => s + t.responseTime, 0) / itemTimings.length : 0
          }
        });
        console.log('Post-task battery data saved successfully');
      } catch (error) {
        console.error('Failed to save post-task battery data:', error);
        setError("Failed to save your responses. Please try again.");
        setSaving(false);
        return; // Don't navigate if save fails
      }
    }

    // Tracking
    try {
      tracker.track("postTaskBatteryComplete", {
        derived,
        totalTime,
        sectionsCompleted: totalSections
      });
    } catch {}

    updateProgress(95);
    // Only navigate after successful save
    navigate("/debrief");
  };

  const progress = ((currentSection + 1) / totalSections) * 100;

  // ---------------------------
  // Render
  // ---------------------------
  return (
      <div style={S.container}>
        <div style={S.card}>
          <h2 style={S.h2}>Post-Task Survey</h2>
          <p style={{ color: "#6c757d", marginBottom: 24, fontSize: 16 }}>Section {currentSection + 1} of {totalSections}</p>
          <div style={S.bar}><div style={{ ...S.fill, width: `${progress}%` }} /></div>
          {saving && <div style={{ marginBottom: "24px", padding: "16px", backgroundColor: "#e3f2fd", color: "#0066cc", borderRadius: "8px", border: "2px solid #90caf9", textAlign: "center", fontWeight: 600 }}>Saving your responses...</div>}
          {error && <div style={S.error}>{error}</div>}

          {/* Sections */}
          {currentSection === 0 && (
              <section style={S.section}>
                <h3 style={S.h3}>Decision Approach</h3>

                <div style={{ marginBottom: 32 }}>
                  <div id="R1-label" style={S.q}>When a workplace message or technology prompt looks professional and familiar, I am inclined to trust it even before checking details.</div>
                  <LikertQuestion name="R1" value={formData.R1} onChange={v => onLikert("R1", v)} />
                </div>

                <div style={{ marginBottom: 32 }}>
                  <div id="R2-label" style={S.q}>When something looks normal but a specific source detail—such as a sender domain, publisher, or device identity—does not match, I hold off until I verify it.</div>
                  <LikertQuestion name="R2" value={formData.R2} onChange={v => onLikert("R2", v)} />
                </div>

                <div style={{ marginBottom: 32 }}>
                  <div id="R3-label" style={S.q}>In general, my initial reaction is based on overall impression rather than technical verification.</div>
                  <LikertQuestion name="R3" value={formData.R3} onChange={v => onLikert("R3", v)} />
                </div>
              </section>
          )}

          {/* DELETED SECTIONS (not tied to H1-H4):
              - Section 1: Error Preferences (E1, E2) - SDT, not SCT
              - Section 2: Familiar Templates (T1) - weak theory link
              - Section 3: Tool Ease (V1, V2) - irrelevant
              - Section 4: Conflicting Cues (C1, C2) - redundant with Section 3 manipulation checks
              - Section 5: Email Experience (B1) - single exploratory question
          */}

          {currentSection === 1 && (
              <section style={S.section}>
                <h3 style={S.h3}>Demographics</h3>
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Age</div>
                  <SelectQuestion 
                    name="age" 
                    value={formData.age} 
                    onChange={v => onSelect("age", v)}
                    options={[
                      { value: '', label: 'Select age range' },
                      { value: '18-24', label: '18-24' },
                      { value: '25-34', label: '25-34' },
                      { value: '35-44', label: '35-44' },
                      { value: '45-54', label: '45-54' },
                      { value: '55-64', label: '55-64' },
                      { value: '65+', label: '65 or older' }
                    ]}
                  />
                </div>
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Gender</div>
                  <SelectQuestion 
                    name="gender" 
                    value={formData.gender} 
                    onChange={v => onSelect("gender", v)}
                    options={[
                      { value: '', label: 'Select gender' },
                      { value: 'male', label: 'Male' },
                      { value: 'female', label: 'Female' },
                      { value: 'non-binary', label: 'Non-binary' },
                      { value: 'prefer-not-to-say', label: 'Prefer not to say' }
                    ]}
                  />
                </div>
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Highest Education Level</div>
                  <SelectQuestion 
                    name="education" 
                    value={formData.education} 
                    onChange={v => onSelect("education", v)}
                    options={[
                      { value: '', label: 'Select education level' },
                      { value: 'high-school', label: 'High School or equivalent' },
                      { value: 'some-college', label: 'Some College' },
                      { value: 'bachelors', label: "Bachelor's Degree" },
                      { value: 'masters', label: "Master's Degree" },
                      { value: 'doctorate', label: 'Doctorate' },
                      { value: 'other', label: 'Other' }
                    ]}
                  />
                </div>
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>How would you rate your computer expertise?</div>
                  <SelectQuestion 
                    name="computerExpertise" 
                    value={formData.computerExpertise} 
                    onChange={v => onSelect("computerExpertise", v)}
                    options={[
                      { value: '', label: 'Select level' },
                      { value: 'beginner', label: 'Beginner' },
                      { value: 'intermediate', label: 'Intermediate' },
                      { value: 'advanced', label: 'Advanced' },
                      { value: 'expert', label: 'Expert' }
                    ]}
                  />
                </div>
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>How often do you use email for work?</div>
                  <SelectQuestion 
                    name="emailUsageFrequency" 
                    value={formData.emailUsageFrequency} 
                    onChange={v => onSelect("emailUsageFrequency", v)}
                    options={[
                      { value: '', label: 'Select frequency' },
                      { value: 'rarely', label: 'Rarely (less than once a week)' },
                      { value: 'occasionally', label: 'Occasionally (few times a week)' },
                      { value: 'daily', label: 'Daily' },
                      { value: 'hourly', label: 'Multiple times per day' }
                    ]}
                  />
                </div>
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Organization Size</div>
                  <SelectQuestion 
                    name="organizationSize" 
                    value={formData.organizationSize} 
                    onChange={v => onSelect("organizationSize", v)}
                    options={[
                      { value: '', label: 'Select size' },
                      { value: 'small', label: 'Small (1-50 employees)' },
                      { value: 'medium', label: 'Medium (51-250 employees)' },
                      { value: 'large', label: 'Large (251-1000 employees)' },
                      { value: 'enterprise', label: 'Enterprise (1000+ employees)' }
                    ]}
                  />
                </div>
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Industry</div>
                  <SelectQuestion
                    name="industry"
                    value={formData.industry}
                    onChange={v => onSelect("industry", v)}
                    options={[
                      { value: '', label: 'Select industry' },
                      { value: 'technology', label: 'Technology' },
                      { value: 'healthcare', label: 'Healthcare' },
                      { value: 'finance', label: 'Finance/Banking' },
                      { value: 'education', label: 'Education' },
                      { value: 'government', label: 'Government' },
                      { value: 'retail', label: 'Retail' },
                      { value: 'manufacturing', label: 'Manufacturing' },
                      { value: 'consulting', label: 'Consulting' },
                      { value: 'legal', label: 'Legal' },
                      { value: 'other', label: 'Other' }
                    ]}
                  />
                  {formData.industry === 'other' && (
                    <div style={{ marginTop: 16 }}>
                      <input
                        type="text"
                        placeholder="Please specify your industry"
                        value={formData.otherIndustry || ''}
                        onChange={e => onSelect('otherIndustry', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          fontSize: '16px',
                          border: '2px solid #e9ecef',
                          borderRadius: '8px',
                          backgroundColor: 'white'
                        }}
                      />
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Job Role</div>
                  <SelectQuestion
                    name="jobRole"
                    value={formData.jobRole}
                    onChange={v => onSelect("jobRole", v)}
                    options={[
                      { value: '', label: 'Select role' },
                      { value: 'executive', label: 'Executive/Management' },
                      { value: 'it-technical', label: 'IT/Technical' },
                      { value: 'software-engineering', label: 'Software Engineering' },
                      { value: 'administrative', label: 'Administrative' },
                      { value: 'finance-accounting', label: 'Finance/Accounting' },
                      { value: 'sales-marketing', label: 'Sales/Marketing' },
                      { value: 'other', label: 'Other' }
                    ]}
                  />
                  {formData.jobRole === 'other' && (
                    <div style={{ marginTop: 16 }}>
                      <input
                        type="text"
                        placeholder="Please specify your job role"
                        value={formData.otherJobRole || ''}
                        onChange={e => onSelect('otherJobRole', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          fontSize: '16px',
                          border: '2px solid #e9ecef',
                          borderRadius: '8px',
                          backgroundColor: 'white'
                        }}
                      />
                    </div>
                  )}
                </div>
              </section>
          )}

          {currentSection === 2 && (
              <section style={S.section}>
                {/* Student email — required for UCCS (extra credit), hidden for other orgs */}
                {getOrgConfig().orgId === 'uccs' && (
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Please enter your student email ID <span style={{ color: '#c53030' }}>*</span></div>
                  <input
                    type="email"
                    placeholder="e.g., student@uccs.edu"
                    value={formData.studentEmail || ''}
                    onChange={e => onSelect('studentEmail', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '16px',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      backgroundColor: 'white'
                    }}
                  />
                </div>
                )}

                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Approximately how many hours per week do you spend managing email for work?</div>
                  <SelectQuestion
                    name="emailHoursPerWeek"
                    value={formData.emailHoursPerWeek}
                    onChange={v => onSelect("emailHoursPerWeek", v)}
                    options={[
                      { value: '', label: 'Select hours per week' },
                      { value: '0-5', label: '0-5 hours' },
                      { value: '6-10', label: '6-10 hours' },
                      { value: '11-20', label: '11-20 hours' },
                      { value: '21-30', label: '21-30 hours' },
                      { value: '30+', label: '30+ hours' }
                    ]}
                  />
                </div>

                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>How confident are you in your ability to identify phishing emails?</div>
                  <SelectQuestion
                    name="phishingConfidence"
                    value={formData.phishingConfidence}
                    onChange={v => onSelect("phishingConfidence", v)}
                    options={[
                      { value: '', label: 'Select confidence level' },
                      { value: '1', label: '1 - Not at all confident' },
                      { value: '2', label: '2 - Slightly confident' },
                      { value: '3', label: '3 - Moderately confident' },
                      { value: '4', label: '4 - Very confident' },
                      { value: '5', label: '5 - Extremely confident' }
                    ]}
                  />
                </div>

                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>In the past year, approximately how many phishing emails have you successfully identified?</div>
                  <SelectQuestion
                    name="phishingIdentified"
                    value={formData.phishingIdentified}
                    onChange={v => onSelect("phishingIdentified", v)}
                    options={[
                      { value: '', label: 'Select number identified' },
                      { value: '0', label: '0' },
                      { value: '1-2', label: '1-2' },
                      { value: '3-5', label: '3-5' },
                      { value: '6-10', label: '6-10' },
                      { value: '10+', label: '10+' }
                    ]}
                  />
                </div>

                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Does your job involve cybersecurity or IT security responsibilities?</div>
                  <SelectQuestion
                    name="securityRole"
                    value={formData.securityRole}
                    onChange={v => onSelect("securityRole", v)}
                    options={[
                      { value: '', label: 'Select security role involvement' },
                      { value: 'none', label: 'No' },
                      { value: 'minor', label: 'Minor responsibilities' },
                      { value: 'moderate', label: 'Moderate responsibilities' },
                      { value: 'major', label: 'Major responsibilities' },
                      { value: 'primary', label: 'Primary job function' }
                    ]}
                  />
                </div>

                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Have you been targeted by a phishing attack before? (Please select the option that best describes your experience)</div>
                  <SelectQuestion
                    name="phishingExperience"
                    value={formData.phishingExperience}
                    onChange={v => onSelect("phishingExperience", v)}
                    options={[
                      { value: '', label: 'Select your experience' },
                      { value: 'never', label: 'Never - I have not been targeted by phishing attacks' },
                      { value: 'once', label: 'Once - I have been targeted once that I know of' },
                      { value: 'few-times', label: 'A few times - I have been targeted multiple times' },
                      { value: 'many-times', label: 'Many times - I frequently receive phishing attempts' },
                      { value: 'uncertain', label: 'Uncertain - I cannot clearly identify if I have been targeted' }
                    ]}
                  />
                </div>

                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Have you received security awareness training?</div>
                  <SelectQuestion
                    name="securityTraining"
                    value={formData.securityTraining}
                    onChange={v => onSelect("securityTraining", v)}
                    options={[
                      { value: '', label: 'Select option' },
                      { value: 'none', label: 'No training' },
                      { value: 'basic', label: 'Basic training (one-time)' },
                      { value: 'regular', label: 'Regular training (annual)' },
                      { value: 'extensive', label: 'Extensive training (multiple per year)' }
                    ]}
                  />
                </div>
              </section>
          )}

          {currentSection === 3 && (
              <section style={S.section}>
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Overall, how difficult was it to distinguish appropriate from risky actions across the email and device-security scenarios?</div>
                  <SelectQuestion 
                    name="taskDifficulty" 
                    value={formData.taskDifficulty} 
                    onChange={v => onSelect("taskDifficulty", v)}
                    options={[
                      { value: '', label: 'Select difficulty level' },
                      { value: 'very-easy', label: 'Very easy - most were obvious' },
                      { value: 'easy', label: 'Easy - I felt confident in my decisions' },
                      { value: 'moderate', label: 'Moderate - some required careful thought' },
                      { value: 'difficult', label: 'Difficult - many were hard to judge' },
                      { value: 'very-difficult', label: 'Very difficult - I was often uncertain' }
                    ]}
                  />
                </div>
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Did you notice any scenarios that appeared risky overall but contained details supporting the action, or appeared safe overall but contained warning details?</div>
                  <SelectQuestion
                    name="congruenceAwareness"
                    value={formData.congruenceAwareness}
                    onChange={v => onSelect("congruenceAwareness", v)}
                    options={[
                      { value: '', label: 'Select option' },
                      { value: 'yes-many', label: 'Yes, I noticed this in several scenarios' },
                      { value: 'yes-few', label: 'Yes, I noticed this in a few scenarios' },
                      { value: 'maybe', label: 'Maybe - I\'m not sure' },
                      { value: 'no', label: 'No, the scenarios generally looked as expected' }
                    ]}
                  />
                </div>

                {/* CRITICAL MANIPULATION CHECK 1: Cue Conflict Awareness */}
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Did you notice that some scenarios appeared trustworthy overall but included unusual specific details, such as a sender address, link destination, software publisher, or network identity?</div>
                  <SelectQuestion
                    name="cueConflictNoticed"
                    value={formData.cueConflictNoticed}
                    onChange={v => onSelect("cueConflictNoticed", v)}
                    options={[
                      { value: '', label: 'Select answer' },
                      { value: 'yes-many', label: 'Yes, I noticed this in many scenarios' },
                      { value: 'yes-some', label: 'Yes, I noticed this in some scenarios' },
                      { value: 'maybe', label: 'Maybe - I\'m not sure' },
                      { value: 'no', label: 'No, I didn\'t notice this pattern' }
                    ]}
                  />
                </div>

                {/* CRITICAL MANIPULATION CHECK 2: Conflict Resolution Strategy - Conditional */}
                {(['yes-many', 'yes-some'].includes(formData.cueConflictNoticed)) && (
                  <div style={{ marginBottom: 32 }}>
                    <div style={S.q}>When you noticed these conflicts between appearance and details, which influenced your decision more?</div>
                    <SelectQuestion
                      name="cueConflictResolution"
                      value={formData.cueConflictResolution}
                      onChange={v => onSelect("cueConflictResolution", v)}
                      options={[
                        { value: '', label: 'Select answer' },
                        { value: 'appearance', label: 'Overall appearance or impression' },
                        { value: 'details', label: 'Specific source and security details' },
                        { value: 'both', label: 'Both influenced me equally' },
                        { value: 'varied', label: 'It varied across scenarios' }
                      ]}
                    />
                  </div>
                )}

                {/* CRITICAL MANIPULATION CHECK 3: Actual Processing Mode */}
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Thinking back to how you made decisions in this study, which best describes your approach?</div>
                  <SelectQuestion
                    name="actualProcessingMode"
                    value={formData.actualProcessingMode}
                    onChange={v => onSelect("actualProcessingMode", v)}
                    options={[
                      { value: '', label: 'Select approach' },
                      { value: 'gut-feeling', label: 'I relied mostly on gut feeling / overall impression' },
                      { value: 'careful-analysis', label: 'I carefully analyzed details for each scenario' },
                      { value: 'mix-gut-first', label: 'I used gut feeling first, then checked details if unsure' },
                      { value: 'mix-details-first', label: 'I analyzed details first, then went with my impression' },
                      { value: 'varied', label: 'My approach varied a lot across scenarios' }
                    ]}
                  />
                </div>

                {/* CRITICAL MANIPULATION CHECK 4: Scenario Difficulty Type */}
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Which types of scenarios were harder for you to evaluate?</div>
                  <SelectQuestion
                    name="scenarioDifficultyType"
                    value={formData.scenarioDifficultyType}
                    onChange={v => onSelect("scenarioDifficultyType", v)}
                    options={[
                      { value: '', label: 'Select type' },
                      { value: 'professional', label: 'Professional or safe-looking scenarios were harder' },
                      { value: 'suspicious', label: 'Suspicious or risky-looking scenarios were harder' },
                      { value: 'mixed', label: 'Scenarios with conflicting cues were harder' },
                      { value: 'all-equal', label: 'All were equally difficult' },
                      { value: 'all-easy', label: 'None were particularly difficult' }
                    ]}
                  />
                </div>

                {/* PROCESS MEASURE 1: Gist Extraction Difficulty */}
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>How difficult was it to form a quick overall impression of whether the appropriate action in each email or device-security scenario was safe or risky?</div>
                  <SelectQuestion
                    name="gistExtractionDifficulty"
                    value={formData.gistExtractionDifficulty}
                    onChange={v => onSelect("gistExtractionDifficulty", v)}
                    options={[
                      { value: '', label: 'Select difficulty' },
                      { value: 'very-easy', label: 'Very easy - I instantly knew most of the time' },
                      { value: 'easy', label: 'Easy - I could form a quick impression' },
                      { value: 'moderate', label: 'Moderate - Sometimes easy, sometimes hard' },
                      { value: 'difficult', label: 'Difficult - I struggled to form quick impressions' },
                      { value: 'very-difficult', label: 'Very difficult - I rarely had a gut feeling' }
                    ]}
                  />
                </div>

                {/* PROCESS MEASURE 2: Post-Task Confidence */}
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>Overall, how confident are you that your decisions in this study were correct?</div>
                  <LikertQuestion name="postTaskConfidence" value={formData.postTaskConfidence} onChange={v => onLikert("postTaskConfidence", v)} />
                </div>

                {/* ATTENTION CHECK: Music enjoyment (unrelated to study) */}
                <div style={{ marginBottom: 32 }}>
                  <div style={S.q}>How much do you enjoy listening to music while working?</div>
                  <LikertQuestion name="musicEnjoyment" value={formData.musicEnjoyment} onChange={v => onLikert("musicEnjoyment", v)} />
                </div>
              </section>
          )}

          {/* Navigation */}
          <div style={{ ...S.rowNav, justifyContent: currentSection === 0 ? "flex-end" : "space-between" }}>
            {currentSection > 0 && (
                <button type="button" onClick={prev} style={{ ...S.btn, ...S.btnSecondary }}>Previous</button>
            )}
            {currentSection === totalSections - 1 ? (
                <button type="button" onClick={submit} disabled={saving} style={{ ...S.btn, ...S.btnPrimary, opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Saving...' : 'Complete Survey'}
                </button>
            ) : (
                <button type="button" onClick={next} style={{ ...S.btn, ...S.btnPrimary }}>Next</button>
            )}
          </div>
        </div>
      </div>
  );
};

export default PostTaskBattery;
